import { FormsModule } from '@angular/forms';
import { Component, HostListener, inject, ViewChild, ViewEncapsulation, OnInit } from '@angular/core';
import { DrawerComponent, DrawerMode, DrawerSelectEvent, KENDO_DRAWER } from '@progress/kendo-angular-layout';
import { AppLocale, OptinexUserDTO, UserService } from './core/services/http-services/common/user.service';
import { SVGIcon, chevronDownIcon, chevronUpIcon, logoutIcon, menuIcon } from '@progress/kendo-svg-icons';
import { optimizeIcon } from '@app/shared/helpers/custom-svg-icons';
import { AuthService } from './core/services/auth-service/auth.service';
import { LocalStorageService } from '@app/core/services/custom-services/localstorage.service';
import { CommonModule, Location } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService, TranslateModule, _ } from '@ngx-translate/core';
import { environment } from '@environment/environment';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { UserRoleEnum } from '@app/shared/helpers/role-based-access.helper';
import { filter, Subject, takeUntil } from 'rxjs';
import { CldrIntlService, IntlService } from '@progress/kendo-angular-intl';
import { KENDO_ICONS } from '@progress/kendo-angular-icons';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_APPBAR } from '@progress/kendo-angular-navigation';
import { UserSettingsComponent } from './core/components/user-settings/user-settings.component';
import { NotificationPopupComponent } from './features/app-controls/notification-popup/notification-popup.component';
import '@progress/kendo-angular-intl/locales/de/all';
import '@progress/kendo-angular-intl/locales/en/all';
import '@progress/kendo-angular-intl/locales/sv/all';
import '@progress/kendo-angular-intl/locales/fi/all';
import '@progress/kendo-angular-intl/locales/hi/all';
import { ApplicationInsightsService } from './core/services/custom-services/application-insights.service';
import { TitleService } from './core/services/custom-services/title.service';
import { MenuItemService, AppDrawerItem } from './core/services/custom-services/menu-item.service';
import { KENDO_DIALOGS } from '@progress/kendo-angular-dialog';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { AppNotificationService } from './core/services/custom-services/notification.service';
import { CopilotChatHostComponent } from './core/components/copilot-chat/copilot-chat-host.component';
import { CopilotChatService } from './core/services/custom-services/copilot-chat.service';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-root',
  styles: [
    `
      html,
      body,
      app-root {
        padding: 0;
        height: 100%;
      }

      app-root {
        display: flex;
        flex-direction: column;
      }

      kendo-drawer-container {
        flex: 1 1 auto;
        overflow-y: auto;
        display: flex;
      }

      kendo-drawer-content {
        flex: 1 1 auto;
        overflow-y: scroll;
        overflow-x: scroll;
      }

      .toolbar-logo {
        height: 30px;
      }

      .margined-content {
        padding: 10px;
        height: calc(100vh - 85px);
      }

      .no-margined-content {
        height: 100%;
      }

      .environment-badge {
        background-color: #1e3a5f;
        color: white;
        padding: 4px 12px;
        border-radius: 4px;
        font-weight: 600;
        font-size: 0.85rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
    `,
  ],
  templateUrl: 'app.component.html',
  standalone: true,
  imports: [FormsModule, CommonModule, RouterOutlet, TranslateModule, UserSettingsComponent, NotificationPopupComponent, CopilotChatHostComponent, KENDO_ICONS, KENDO_BUTTONS, KENDO_DRAWER, KENDO_APPBAR, KENDO_DIALOGS, KENDO_LOADER],
})
export class AppComponent implements OnInit {
  public mode: DrawerMode = 'push';

  //Icons
  public menuSvg: SVGIcon = menuIcon;
  public logoutIcon: SVGIcon = logoutIcon;
  public chevronDownIcon: SVGIcon = chevronDownIcon;
  public chevronUpIcon: SVGIcon = chevronUpIcon;
  public optimizeIcon: SVGIcon = optimizeIcon;

  public expanded: boolean = false;
  public loggedIn: boolean = false;
  public loadingUser: boolean = false;

  public applyMargin: boolean = true;

  public configuration = environment;
  hideMenu: boolean = false;

  @ViewChild(DrawerComponent) drawer: DrawerComponent;

  public authenicatedNodes: Array<AppDrawerItem> = [];
  currentUrl: string;
  user: OptinexUserDTO;
  deniedRoles = [UserRoleEnum.Operator];

  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly titleService = inject(TitleService);
  private readonly location = inject(Location);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly translate = inject(TranslateService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly msalService = inject(MsalService);
  private readonly intlService = inject(IntlService);
  private readonly applicationInsightsService = inject(ApplicationInsightsService);
  private readonly menuItemService = inject(MenuItemService);
  private readonly notificationService = inject(AppNotificationService);
  private readonly copilotChat = inject(CopilotChatService);

  private readonly languageMap: Record<string, string> = {
    us: 'en',
    eu: 'en',
    de: 'de',
    sv: 'sv',
    hi: 'hi',
    fi: 'fi',
    mt: 'empty',
  };

  constructor() {
    this.currentUrl = this.location.path().slice(1);
  }

  public isItemExpanded = (item: any): boolean => {
    return item.expanded;
  };

  private readonly _destroying$ = new Subject<void>();

  ngOnInit() {
    this.msalService.handleRedirectObservable().subscribe();

    this.router.events.subscribe(data => {
      if (data instanceof NavigationEnd) {
        this.applyMargin = true;
        if (data.url == '/' || data.url == '/home') {
          this.applyMargin = false;
        }
        // Update currentUrl and menu selection on navigation
        this.currentUrl = data.url.slice(1); // Remove leading slash
        if (this.loggedIn) {
          this.setMenuSelection();
        }
      }
    });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(async () => {
        const allAccounts = this.msalService.instance.getAllAccounts();

        if (allAccounts?.length > 0) {
          this.loggedIn = true;

          let activeAccount = this.msalService.instance.getActiveAccount();

          if (!activeAccount) {
            this.msalService.instance.setActiveAccount(allAccounts[0]);
          }
          this.applicationInsightsService.setUserId(allAccounts[0].username, allAccounts[0].tenantId);

          try {
            let user = await this.userService.getUser();
            this.applyUser(user);
          } catch (err) {
            this.notificationService.notifyWarningAppChanel('Failed to get user, Contact Support', err);
          }

          this.titleService.resetTitle();
        }
      });
  }

  applyUser(user: OptinexUserDTO) {
    this.user = user;

    this.setLang(this.userService.getLocaleByCode(user.languageCode, user.localeCode));

    this.authenicatedNodes = this.menuItemService.filterNodesByUser(user);

    this.loggedIn = true;

    // Navigate to default route if on home/root, otherwise just set menu selection
    if (this.currentUrl === '' || this.currentUrl === 'home') {
      this.router.navigate(['/operative/prod-order']);
    } else {
      this.setMenuSelection();
    }
  }

  public setLang(locale: AppLocale): void {
    let currentLocale = this.userService.getLocaleFromLocalStorage();

    /*if (currentLocale && currentLocale.locale == locale.locale && currentLocale.language == locale.language) {
      console.info('Using Locale from LocalStorage');
      return;
    }*/

    console.info('Switching Locale');

    const language = this.languageMap[locale.language] ?? 'en';

    this.translate.use(language).subscribe(() => {
      this.localStorageService.localeChanged();
    });

    this.user.languageCode = locale.language.toLocaleLowerCase();
    this.user.localeCode = locale.locale;

    if (this.intlService instanceof CldrIntlService) {
      // Used by kendo-components
      this.intlService.localeId = locale.locale;
    }

    this.userService.setLocale(locale);
  }

  setMenuSelection() {
    this.authenicatedNodes.forEach(x => {
      x.selected = false;
      x.expanded = false;
    });

    let selectedNode = this.authenicatedNodes.find(n => n.path === this.currentUrl);

    if (selectedNode) {
      selectedNode.selected = true;
      this.expandParents(selectedNode);
    }

    this.authenicatedNodes = [...this.authenicatedNodes];
  }

  onRightClick(item: AppDrawerItem) {
    if (item.path) {
      window.open(item.path, '_blank');
    }
  }

  public onSelect(ev: DrawerSelectEvent): void {
    if (ev.item.path) {
      this.authenicatedNodes.forEach(x => {
        x.selected = false;
        x.expanded = false;
      });

      ev.item.selected = true;

      this.currentUrl = ev.item.path;

      this.router.navigate([this.currentUrl]);

      this.setMenuSelection();

      ev.sender.toggle(false);
    } else {
      ev.item.expanded = !ev.item.expanded;
    }
  }

  private expandParents(item: AppDrawerItem) {
    let parentId = item.parentId;

    while (parentId) {
      let parrent = this.authenicatedNodes.find(x => x.id == parentId);

      parrent.expanded = true;

      parentId = parrent.parentId;
    }
  }

  public toggleExpanded() {
    this.drawer.toggle();
    this.setMenuSelection();
  }

  async login() {
    this.authService.login();
  }

  logout() {
    this.copilotChat.reset();
    this.authService.logout();
  }

  ngAfterViewInit(): void {
    if (this.user.userSites.length === 0) {
      this.hideMenu = true;
      this.router.navigate(['/page-not-found']);
    }
  }

  @HostListener('click', ['$event.target']) onClick(e) {
    if (e.className.indexOf('k-overlay') >= 0) {
      this.drawer.toggle(false);
    }
  }
}
